import { useState, useRef } from 'react';
import { type QueryLazyOptions, useLazyQuery } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';
import { fireIssueCreateErrorAnalytics as fireErrorAnalytics } from '@atlassian/jira-issue-create-analytics-common/src/utils/fire-error-analytics/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { handleApolloError } from '@atlassian/jira-issue-create-commons/src/common/utils/handle-apollo-error/index.tsx';
import type {
	QueryVariables,
	CreateSubtaskConfigQueryResponse,
	FetchConfigData,
	CreateSubtaskConfigQueryResponseWithML,
} from '../../common/types/index.tsx';
import {
	transformQueryResults,
	tranformSubtaskData,
} from '../../common/utils/fetch-config-query/index.tsx';
import { useSmartFieldsMachineLearning } from '../../common/utils/use-smart-fields-ml/index.tsx';
import { CREATE_SUBTASK_CONFIG_USER_PREFERRED_QUERY } from './gql-old/index.tsx';
import { getCreateSubtaskConfigUserPreferredQuery } from './gql.tsx';
import type { UseFetchSubtaskCreateConfigQuery } from './types.tsx';

const useFetchSubtaskCreateConfigQuery: UseFetchSubtaskCreateConfigQuery = (props = {}) => {
	const { displayOnlyRequiredFields = false, triggerPointKey } = props;
	const [loading, setLoading] = useState(false);
	const [fetchError, setFetchError] = useState<ApolloError | null>(null);
	const apiStartTime = useRef<number>(0);
	const [apiLatency, setApiLatency] = useState<number>(0);
	const [data, setData] = useState<FetchConfigData>({
		fieldTabs: [],
		fieldConfig: {},
		contextFields: {},
		meta: {
			areRequiredFieldsSupported: true,
			queryErrorMessage: '',
		},
		additionalServices: {},
	});

	const { withMachineLearning } = fg('migrate_gic_smart_fields_jira_labs')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useSmartFieldsMachineLearning()
		: { withMachineLearning: false };

	const [fetchSubtaskConfig] = useLazyQuery<
		CreateSubtaskConfigQueryResponse | CreateSubtaskConfigQueryResponseWithML,
		QueryVariables
	>(
		fg('migrate_gic_smart_fields_jira_labs')
			? getCreateSubtaskConfigUserPreferredQuery(withMachineLearning)
			: CREATE_SUBTASK_CONFIG_USER_PREFERRED_QUERY,
		{
			fetchPolicy: 'no-cache',
			onCompleted: (queryData) => {
				setApiLatency(Math.floor(performance.now() - apiStartTime.current));
				setData(transformQueryResults(tranformSubtaskData(queryData), displayOnlyRequiredFields));
				setLoading(false);
			},
			onError: (error: ApolloError) => {
				setFetchError(error);
				fireErrorAnalytics({
					meta: {
						id: 'useFetchSubtaskCreateConfigQuery',
						packageName: 'jiraIssueCreate',
						teamName: 'gryffindor',
					},
					error,
					attributes: {
						// TODO: clean up after rollout of FG issue-create-fire-error-analytics-refactor
						errorType: handleApolloError(error),
						// TODO: look to get this into common hook and populate it everywhere.
						triggerPointKey,
					},
					sendToPrivacyUnsafeSplunk: true,
				});
			},
		},
	);
	const handleFetch = useRef((queryVariables?: QueryLazyOptions<QueryVariables>) => {
		setLoading(true);
		apiStartTime.current = performance.now();
		fetchSubtaskConfig(queryVariables);
	});
	return [
		{
			isLoading: loading,
			fieldTabs: data.fieldTabs,
			fieldConfig: data.fieldConfig,
			contextFields: data.contextFields,
			meta: data.meta,
			error: fetchError,
			apiLatency,
			additionalServices: data.additionalServices,
		},
		handleFetch.current,
	];
};
export default useFetchSubtaskCreateConfigQuery;
